import {
  BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT,
  BOT_SEQUENCE_STEP_LIMITS,
  BotSequenceConfigStep,
  BotSequenceConfigStepDelivery,
  BotSequenceConfigStepEmail,
  BotSequenceContentTemplate,
  BotSequenceDeliveryChannel,
  BotSequenceType,
  Channel
} from "@swivl-mono/types";
import { Select, SelectSize, Switch, Tooltip, VariableTypeahead } from "@swivl-mono/ui";

import { useSequencesV3Context } from "../SequencesV3.Context";
import { priorTemplateStep, templateEmail } from "../ladderDraft";
import { SEQUENCE_TYPE_META } from "../sequenceTypeMeta";
import { DeliveryEditor } from "./DeliveryEditor";
import { useSequenceVariables } from "./LocaleBodiesEditor";
import { RevertToDefault } from "./RevertToDefault";
import { TemplateSelect, confirmTemplateSwitch } from "./TemplateSelect";

export interface DayCapabilityNotes {
  /** Why the FMS note cannot be turned on here; null = allowed. */
  fmsNoteDisabledReason: string | null;
  /** Extra context when the note applies to only some locations in the scope. */
  fmsNoteInfo: string | null;
  /** Why email cannot be turned on here (no Mandrill App for the org); null = allowed. */
  emailDisabledReason: string | null;
}

interface DaySettingsProps {
  sequenceType: BotSequenceType;
  step: BotSequenceConfigStep;
  /** The whole ladder, so templates can carry earlier edits forward. */
  ladder: BotSequenceConfigStep[];
  disabled: boolean;
  capability: DayCapabilityNotes;
  onChange: (patch: Partial<BotSequenceConfigStep>) => void;
}

interface SettingRowProps {
  title: string;
  caption: string;
  tooltip?: string;
  on: boolean;
  disabled: boolean;
  onSet: (on: boolean) => void;
  /** Shown beside the switch while the row is on. */
  headerExtra?: React.ReactNode;
  children?: React.ReactNode;
}

function SettingRow({ title, caption, tooltip, on, disabled, onSet, headerExtra, children }: SettingRowProps): React.ReactElement {
  return (
    <section className="rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="body-sm-alt flex items-center gap-1.5 text-grey-900">
            {title}
            {tooltip && (
              <Tooltip content={tooltip}>
                <span
                  className="body-xs inline-flex size-4 items-center justify-center rounded-full border border-grey-400 text-grey-600"
                  aria-label="More information"
                >
                  ?
                </span>
              </Tooltip>
            )}
          </div>
          <div className="body-xs text-grey-600">{caption}</div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {on && headerExtra}
          <Switch on={on} onSet={onSet} isDisabled={disabled} activeColor="orange" />
        </div>
      </div>
      {on && children && <div className="flex flex-col gap-3 border-t border-grey-100 px-4 py-3">{children}</div>}
    </section>
  );
}

const ALWAYS_SEND_TOOLTIP =
  "Sends on this exact day even on weekends, holidays or when Sequence Sending Hours are closed, and ignores the minimum gap and agent suppression. Quiet hours are never broken: if the day's legal window has already closed, it goes out at the next legal opening and a ticket is opened for this location so staff can follow up.";

const SEND_IMMEDIATELY_TOOLTIP = "Goes out at the first allowed moment on this day instead of a spread-out time.";

const NO_STARTING_SCRIPT = "";

/**
 * Starting script (ENG-3403): the call script the bot begins with when the customer replies to
 * this day's message. The bot's other scripts still apply if the customer takes the conversation
 * elsewhere; a script marked "for sequences only" appears only this way.
 */
function StartingScriptRow({
  value,
  disabled,
  onSet
}: {
  value: string | undefined;
  disabled: boolean;
  onSet: (slug: string | undefined) => void;
}): React.ReactElement {
  const { scripts, loading } = useSequencesV3Context().scripts;
  const options = [{ label: "No starting script", value: NO_STARTING_SCRIPT }, ...scripts.map((s) => ({ label: s.name, value: s.slug }))];
  const current = value && scripts.some((s) => s.slug === value) ? value : NO_STARTING_SCRIPT;
  return (
    <section className="rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="body-sm-alt text-grey-900">Starting script</div>
          <div className="body-xs text-grey-500">
            When the customer replies to this day&apos;s message, the bot begins with this call script. Its other scripts still apply if the
            conversation goes elsewhere.
            {value && current === NO_STARTING_SCRIPT && !loading && <span className="text-red-600"> The script set here is no longer in force.</span>}
          </div>
        </div>
        <div className="w-64 shrink-0">
          <Select
            size={SelectSize.MEDIUM}
            hideClear
            disabled={disabled || loading}
            selectedValues={[current]}
            options={options}
            onChange={(values) => {
              const next = values[0] ?? NO_STARTING_SCRIPT;
              onSet(next === NO_STARTING_SCRIPT ? undefined : next);
            }}
          />
        </div>
      </div>
    </section>
  );
}

/** Everything one day does: SMS, voice call, email, FMS note, and the two day settings. All settings, not actions (decision 33). */
export function DaySettings({ sequenceType, step, ladder, disabled, capability, onChange }: DaySettingsProps): React.ReactElement {
  const variables = useSequenceVariables();
  const deliveries: BotSequenceConfigStepDelivery[] = step.deliveries ?? [{ channel: Channel.SMS, content: { mode: "generated" } }];
  const deliveryFor = (channel: BotSequenceDeliveryChannel): BotSequenceConfigStepDelivery | undefined =>
    deliveries.find((delivery) => delivery.channel === channel);

  const handleDelivery = (channel: BotSequenceDeliveryChannel, next: BotSequenceConfigStepDelivery | undefined): void => {
    const others = deliveries.filter((delivery) => delivery.channel !== channel);
    const merged = next ? [...others, next] : others;
    // Keep SMS before Voice for a stable display order.
    merged.sort((left, right) => (left.channel === Channel.SMS ? -1 : right.channel === Channel.SMS ? 1 : 0));
    onChange({ deliveries: merged });
  };

  const getPriorStep = (template: BotSequenceContentTemplate) => priorTemplateStep(ladder, step.offsetDays, template);

  const patchEmail = (patch: Partial<BotSequenceConfigStepEmail>): void =>
    onChange({
      email: { enabled: true, subject: step.email?.subject ?? "", body: step.email?.body ?? "", template: step.email?.template, ...patch }
    });

  // Switched on blank, the email starts as its template's wording (decision 53).
  const handleEmailToggle = (enabled: boolean): void => {
    const defaults = templateEmail(sequenceType, step.offsetDays, step.email?.template);
    patchEmail({
      enabled,
      subject: step.email?.subject?.trim() ? step.email.subject : defaults.subject,
      body: step.email?.body?.trim() ? step.email.body : defaults.body
    });
  };

  const handleEmailTemplateChange = (template: BotSequenceContentTemplate | undefined): void => {
    const current = templateEmail(sequenceType, step.offsetDays, step.email?.template);
    const isEdited = Boolean(step.email?.body?.trim()) && (step.email?.subject !== current.subject || step.email?.body !== current.body);
    if (!confirmTemplateSwitch(isEdited)) return;
    patchEmail({ template, ...templateEmail(sequenceType, step.offsetDays, template, template && getPriorStep(template)) });
  };

  const emailDefaults = templateEmail(sequenceType, step.offsetDays, step.email?.template);
  const isEmailCustomized = step.email !== undefined && (step.email.subject !== emailDefaults.subject || step.email.body !== emailDefaults.body);
  const isFmsNoteCustomized = step.fmsNote?.text !== undefined && step.fmsNote.text !== BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT;

  const handleFmsNoteToggle = (enabled: boolean): void => {
    const text = step.fmsNote?.text && step.fmsNote.text.trim().length > 0 ? step.fmsNote.text : BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT;
    onChange({ fmsNote: { enabled, text } });
  };

  return (
    <div className="flex flex-col gap-3">
      <DeliveryEditor
        channel={Channel.SMS}
        delivery={deliveryFor(Channel.SMS)}
        sequenceType={sequenceType}
        offsetDays={step.offsetDays}
        disabled={disabled}
        getPriorStep={getPriorStep}
        onChange={(next) => handleDelivery(Channel.SMS, next)}
      />
      <DeliveryEditor
        channel={Channel.VOICE}
        delivery={deliveryFor(Channel.VOICE)}
        sequenceType={sequenceType}
        offsetDays={step.offsetDays}
        disabled={disabled}
        getPriorStep={getPriorStep}
        onChange={(next) => handleDelivery(Channel.VOICE, next)}
      />

      <SettingRow
        title="Email"
        caption={capability.emailDisabledReason ?? "A one-way email to the tenant, sent from this organization's Mandrill account."}
        on={step.email?.enabled === true && capability.emailDisabledReason === null}
        disabled={disabled || capability.emailDisabledReason !== null}
        onSet={handleEmailToggle}
      >
        <div className="flex items-center justify-between gap-3">
          <TemplateSelect value={step.email?.template} disabled={disabled} onChange={handleEmailTemplateChange} />
          <RevertToDefault isCustomized={isEmailCustomized} disabled={disabled} onRevert={() => patchEmail(emailDefaults)} />
        </div>
        <VariableTypeahead
          label="Subject"
          value={step.email?.subject ?? ""}
          onChange={(subject) => patchEmail({ subject })}
          variables={variables}
          multiline={false}
          maxLength={BOT_SEQUENCE_STEP_LIMITS.emailSubject}
          disabled={disabled}
        />
        <VariableTypeahead
          label="Body"
          value={step.email?.body ?? ""}
          onChange={(body) => patchEmail({ body })}
          variables={variables}
          maxLength={BOT_SEQUENCE_STEP_LIMITS.emailBody}
          disabled={disabled}
          placeholder="Type {{ to insert a variable"
        />
      </SettingRow>

      <SettingRow
        title="Note in the FMS"
        caption={capability.fmsNoteDisabledReason ?? capability.fmsNoteInfo ?? "Written on the tenant in the FMS once this day has gone out."}
        on={step.fmsNote?.enabled === true && capability.fmsNoteDisabledReason === null}
        disabled={disabled || capability.fmsNoteDisabledReason !== null}
        onSet={handleFmsNoteToggle}
        headerExtra={
          <RevertToDefault
            isCustomized={isFmsNoteCustomized}
            disabled={disabled}
            onRevert={() => onChange({ fmsNote: { enabled: true, text: BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT } })}
          />
        }
      >
        <VariableTypeahead
          value={step.fmsNote?.text ?? BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT}
          onChange={(text) => onChange({ fmsNote: { enabled: true, text } })}
          variables={variables}
          maxLength={BOT_SEQUENCE_STEP_LIMITS.fmsNoteText}
          disabled={disabled}
          helperText="Type {{ to insert a variable. A variable with no value is written as (unavailable)."
        />
      </SettingRow>

      {SEQUENCE_TYPE_META[sequenceType].offersSendImmediately && (
        <SettingRow
          title="Send immediately"
          caption="Only offered on Move In and Move Out."
          tooltip={SEND_IMMEDIATELY_TOOLTIP}
          on={step.sendASAP === true}
          disabled={disabled}
          onSet={(sendASAP) => onChange({ sendASAP })}
        />
      )}

      <StartingScriptRow value={step.callScriptSlug} disabled={disabled} onSet={(callScriptSlug) => onChange({ callScriptSlug })} />

      <SettingRow
        title="Always send on this day"
        caption="Goes out on this exact date regardless of weekends, holidays, or send-hour closures."
        tooltip={ALWAYS_SEND_TOOLTIP}
        on={step.deferrable === false}
        disabled={disabled}
        onSet={(always) => onChange({ deferrable: !always })}
      />
    </div>
  );
}
